export const vocalsHindustaniSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation in Rāga & Swara)",
    objective:
      "To establish Indian classical voice culture, swara purity, basic raga recognition and time discipline through foundational Hindustani ragas.",
    examFormat: {
      title: "Level 1 — Practical Examination Only",
      totalMarks: 100,
      components: [
        { criteria: "Swara Shuddhi (Pitch Accuracy)", marks: 30 },
        { criteria: "Raga Identity & Grammar", marks: 25 },
        { criteria: "Tala Awareness (Teentaal)", marks: 15 },
        { criteria: "Voice Culture & Tone Quality", marks: 15 },
        { criteria: "Bandish & Sargam Clarity", marks: 15 },
      ],
      notes: "Candidate may sing any 4–6 ragas from prescribed list. Tanpura/drone mandatory. No alap required (optional short phrases allowed). Notation NOT required.",
      passCriteria: "Minimum 50%",
    },
    practicalSections: [
      {
        title: "Voice culture & technique",
        items: [
          "Correct sitting posture (traditional)",
          "Relaxed jaw, neck and shoulders",
          "Natural diaphragmatic breathing",
          "Sustained tone without strain",
        ],
      },
      {
        title: "Swara foundation",
        items: [
          "Shadja (Sa) as tonal centre",
          "Clarity of shuddha swaras",
          "Ascending and descending swara patterns",
          "Singing with tanpura / drone",
        ],
      },
      {
        title: "Rāga study (6 rāgas)",
        items: [
          "Each rāga to be studied with Aroha–Avaroha, basic Pakad, one Bandish and one Sargam Geet",
          "Suggested level-appropriate rāgas: Bilawal, Yaman, Kafi, Bhupali, Khamaj, Asavari (as per board list)",
        ],
      },
      {
        title: "Laya & tāla",
        items: [
          "Introduction to Teentaal",
          "Clapping and counting mātras",
          "Singing bandish in tāla with Sam awareness",
          "Steady laya control",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "Singing of prescribed rāgas as selected by examiner",
          "Clear swara pronunciation and pitch stability",
          "Basic rāga identity and tāla discipline",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "Concept of music as organised sound and silence",
          "Basic idea of swara and sruti",
        ],
      },
      {
        title: "Swara & rāga (conceptual)",
        items: [
          "Shuddha swaras and Sa as tonic",
          "Introduction to the idea of rāga as melodic framework",
        ],
      },
      {
        title: "Tāla & laya",
        items: [
          "Role of tāla in Hindustani music",
          "Basic understanding of Teentaal cycle",
        ],
      },
    ],
    questionBank: {
      title: "Indicative oral prompts – Level 1",
      sections: [
        {
          label: "Oral / viva prompts",
          items: [
            "Name the four strings of the tanpura.",
            "What is Sa?",
            "What is swara shuddhi?",
          ],
        },
      ],
    },
  },
  2: {
    id: 2,
    title: "Level 2 — Basic Development (Rāga Expansion & Control)",
    objective:
      "To develop greater rāga clarity, improved swara control and confident Teentaal handling through additional foundational Hindustani rāgas.",
    examFormat: {
      title: "Level 2 — Practical Examination Only",
      totalMarks: 100,
      components: [
        { criteria: "Swara Shuddhi (Pitch Accuracy)", marks: 30 },
        { criteria: "Raga Identity & Grammar", marks: 25 },
        { criteria: "Tala Awareness (Teentaal)", marks: 15 },
        { criteria: "Voice Culture & Tone Quality", marks: 15 },
        { criteria: "Bandish & Sargam Clarity", marks: 15 },
      ],
      notes: "Candidate may sing any 4–6 ragas from prescribed list. Tanpura/drone mandatory. No alap required (optional short phrases allowed). Notation NOT required.",
      passCriteria: "Minimum 50%",
    },
    practicalSections: [
      {
        title: "Voice & technique",
        items: [
          "Improved vocal stamina",
          "Steady tone across madhya saptak",
          "Reduced breath fluctuation",
        ],
      },
      {
        title: "Rāga study (6 new rāgas)",
        items: [
          "Each rāga with Aroha–Avaroha, clear Pakad, one Bandish and one Sargam Geet",
          "Suggested rāgas: Bihag, Desh, Tilak Kamod, Jaunpuri, Durga, Vrindavani Sarang (as per board list)",
        ],
      },
      {
        title: "Laya & tāla",
        items: [
          "Teentaal full cycle awareness",
          "Recognition of Sam, Khali and Tāli",
          "Singing bandish in slow and medium laya",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "Presentation of multiple rāgas (examiner choice)",
          "Accurate rāga grammar and sargam execution",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rāga & thaat (introductory)",
        items: [
          "Basic idea of thaat",
          "Difference between rāga and scale",
        ],
      },
      {
        title: "Tāla theory",
        items: [
          "Structure of Teentaal",
          "Concept of Sam and Khali",
        ],
      },
    ],
    questionBank: {
      title: "Indicative oral prompts – Level 2",
      sections: [
        {
          label: "Oral / viva prompts",
          items: [
            "Define Teentaal.",
            "What is Sam?",
            "Name any three Hindustani rāgas.",
          ],
        },
      ],
    },
  },
  3: {
    id: 3,
    title: "Level 3 — Intermediate (Advanced Rāga & Music History Introduction)",
    objective:
      "To achieve intermediate performance maturity and introduce musicology, rāga origins and written evaluation.",
    examFormat: {
      title: "Level 3 — Theory + Practical Examination",
      totalMarks: 100,
      practicalMarks: 70,
      theoryMarks: 30,
      components: [
        { criteria: "Practical — Raga Grammar & Authenticity", marks: 20 },
        { criteria: "Practical — Alap Development (Basic)", marks: 15 },
        { criteria: "Practical — Bandish & Sargam Execution", marks: 15 },
        { criteria: "Practical — Tala Accuracy (Teentaal / Ektaal)", marks: 10 },
        { criteria: "Practical — Voice Culture & Presentation", marks: 10 },
        { criteria: "Theory — Short Answers", marks: 10 },
        { criteria: "Theory — Descriptive Questions", marks: 15 },
        { criteria: "Theory — Viva / Oral", marks: 5 },
      ],
      notes: "Separate pass required in theory & practical.",
      passCriteria: "Minimum 55% (separate pass in theory & practical)",
    },
    practicalSections: [
      {
        title: "Rāga study (advanced group)",
        items: [
          "Study of a set of 9 advanced rāgas approved by the board",
          "For each: Aroha–Avaroha, Pakad, one Bandish, basic unmetered Alap and sargam patterns",
        ],
      },
      {
        title: "Laya & tāla",
        items: [
          "Teentaal in detail",
          "Introduction to Ektaal",
          "Medium laya control and simple layakari",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "20–25 minute performance including alap, bandish and sargam",
          "One rāga in more detailed presentation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music history & origin",
        items: [
          "Origin of Indian classical music with reference to Vedic tradition",
          "Role of Sāma Veda in Indian music",
          "Evolution of the rāga system",
          "Basic concept of gharana",
        ],
      },
      {
        title: "Rāga theory",
        items: [
          "Introduction to thaat system",
          "Rāga vs scale distinction",
          "Time theory of rāgas (basic awareness)",
        ],
      },
      {
        title: "Tāla theory",
        items: [
          "Teentaal structure",
          "Ektaal structure",
          "Types of laya",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 3",
      sections: [
        {
          label: "Short answer (2–3 marks)",
          items: [
            "What is the role of Sāma Veda in Indian music?",
            "Define rāga.",
            "What is thaat?",
            "Explain Aroha–Avaroha.",
            "What is Teentaal?",
          ],
        },
        {
          label: "Descriptive (5–8 marks)",
          items: [
            "Describe the origin of Indian classical music with reference to the Vedas.",
            "Difference between rāga and scale.",
            "Explain the time theory of rāgas.",
            "Describe the structure of Teentaal.",
            "Discuss the importance of swara shuddhi in rāga singing.",
          ],
        },
      ],
    },
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 1 (Rāga Depth, Alap & Tān)",
    objective:
      "To develop advanced rāga elaboration skills, including more detailed alap, tāns, deeper tāla understanding and expanded theoretical knowledge.",
    examFormat: {
      title: "Level 4 — Theory + Practical Examination",
      totalMarks: 100,
      practicalMarks: 70,
      theoryMarks: 30,
      components: [
        { criteria: "Practical — Detailed Alap", marks: 20 },
        { criteria: "Practical — Taans (Clarity & Control)", marks: 15 },
        { criteria: "Practical — Bandish Presentation", marks: 15 },
        { criteria: "Practical — Tala & Layakari", marks: 10 },
        { criteria: "Practical — Aesthetic Expression (Bhava)", marks: 10 },
        { criteria: "Theory — Descriptive Theory", marks: 15 },
        { criteria: "Theory — Raga & Tala Analysis", marks: 10 },
        { criteria: "Theory — Viva / Oral", marks: 5 },
      ],
      notes: "Separate pass required in theory & practical.",
      passCriteria: "Minimum 55% (separate pass in theory & practical)",
    },
    practicalSections: [
      {
        title: "Rāga study (advanced)",
        items: [
          "Study of 9 advanced rāgas as prescribed by the board",
          "For each: Vilambit bandish, detailed alap, sargam and basic–moderate tāns",
        ],
      },
      {
        title: "Laya & tāla",
        items: [
          "Teentaal, Ektaal and Jhaptaal at appropriate speeds",
          "Layakari awareness",
          "Controlled tān execution within tāla",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "30-minute performance including one rāga in full detail and one shorter rāga presentation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced music history",
        items: [
          "Development of Khayāl gayaki",
          "Importance of rāga aesthetics",
          "Rasa theory in rāgas",
        ],
      },
      {
        title: "Tāla & structure",
        items: [
          "Importance of tāla in performance",
          "Laya, layakari and tempo control",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 4",
      sections: [
        {
          label: "Short answer",
          items: [
            "Define Alap.",
            "What are tāns?",
            "What is layakari?",
            "Name any four Hindustani tālas.",
            "What is rāsa in music?",
          ],
        },
        {
          label: "Descriptive",
          items: [
            "Importance of Alap in Khayāl singing.",
            "Development of Khayāl gayaki.",
            "Role of tāla in rāga presentation.",
            "Relationship between rāga and bhāva.",
            "Explain Teentaal and Jhaptaal with structure.",
          ],
        },
      ],
    },
  },
  5: {
    id: 5,
    title: "Level 5 — Professional (Advanced Rāgas & Bada Khayāl)",
    objective:
      "To certify professional-level Hindustani vocal performance with advanced rāga handling and scholarly understanding.",
    examFormat: {
      title: "Level 5 — Professional Certification",
      totalMarks: 100,
      practicalMarks: 80,
      theoryMarks: 20,
      components: [
        { criteria: "Practical — Bada Khayal (Depth & Expansion)", marks: 30 },
        { criteria: "Practical — Alap–Sargam–Taan Mastery", marks: 20 },
        { criteria: "Practical — Raga Authority & Aesthetics", marks: 15 },
        { criteria: "Practical — Performance Stamina (40 min)", marks: 10 },
        { criteria: "Practical — Overall Presentation", marks: 5 },
        { criteria: "Theory — Raga History & Evolution", marks: 10 },
        { criteria: "Theory — Comparative Raga / Gharana Study", marks: 10 },
      ],
      notes: "40-minute solo recital mandatory.",
      passCriteria: "Minimum 60% (mandatory recital completion)",
    },
    practicalSections: [
      {
        title: "Rāga study (15 advanced rāgas)",
        items: [
          "Bada Khayāl (Vilambit) and Madhya / Drut bandish for selected rāgas",
          "Alap, sargam and tāns in advanced rāgas",
          "Elaborate rāga development with aesthetic control",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "40-minute solo recital",
          "One major rāga in full depth and one supporting rāga",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced rāga history & evolution",
        items: [
          "Development of rāga system",
          "Comparative rāga analysis",
        ],
      },
      {
        title: "Gharana styles (overview)",
        items: [
          "Basic awareness of major gharanas and stylistic traits",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 5",
      sections: [
        {
          label: "Long answer / analytical",
          items: [
            "Evolution of Khayāl music in the Hindustani tradition.",
            "Detailed study of Bada Khayāl.",
            "Comparison of two rāgas from different thāts.",
            "Role of gharanas in Hindustani classical music.",
            "Importance of rāga aesthetics in advanced performance.",
          ],
        },
      ],
    },
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Guru Level)",
    objective:
      "To certify complete mastery of Hindustani classical vocal music, combining artistic authority, historical scholarship and performance leadership.",
    examFormat: {
      title: "Level 6 — Mastery / Guru Level",
      totalMarks: 100,
      practicalMarks: 80,
      theoryMarks: 20,
      components: [
        { criteria: "Practical — Concert-Level Raga Presentation", marks: 35 },
        { criteria: "Practical — Artistic Authority & Interpretation", marks: 20 },
        { criteria: "Practical — Advanced Alap & Taan Development", marks: 15 },
        { criteria: "Practical — Stage Leadership & Command", marks: 10 },
        { criteria: "Theory / Research — Written Thesis / Lecture Recital / Viva", marks: 20 },
      ],
      notes: "40–60 minute concert-length performance mandatory. Successful candidates are certified as 'Global Certified Hindustani Classical Vocalist – Mastery Level'.",
      passCriteria: "Minimum 60% (mandatory recital completion)",
    },
    practicalSections: [
      {
        title: "Rāga study (highly advanced rāgas)",
        items: [
          "Bada Khayāl with advanced alap–tān development",
          "Personal rāga interpretation and stylistic maturity",
        ],
      },
      {
        title: "Performance requirement",
        items: [
          "40–60 minute concert-length performance",
          "Full traditional presentation with command over accompanists and audience",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced musicology",
        items: [
          "In-depth study of Vedic origins",
          "Evolution of rāga system from ancient to modern times",
          "Philosophical aesthetics of Indian music",
        ],
      },
    ],
    questionBank: {
      title: "Sample theory question bank – Level 6",
      sections: [
        {
          label: "Advanced / research-based",
          items: [
            "Discuss Vedic philosophy and its influence on Indian classical music.",
            "Trace the evolution of the rāga system.",
            "Examine the aesthetic and spiritual dimensions of rāga music.",
            "Role of the performer as Guru and artist.",
            "Comparative study of rāga interpretation across gharanas.",
          ],
        },
      ],
    },
  },
};
